import { TinyColor } from '@ctrl/tinycolor'
import { ThemeProvider, useMediaQuery } from '@mui/material'
import dayjs from 'dayjs'
import { ConfirmProvider } from 'material-ui-confirm'
import moment from 'moment-timezone'
import { FC, PropsWithChildren, useLayoutEffect } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { createDataset } from '../../helpers/others'
import { useGet } from '../../hooks/get'
import { useSwitch } from '../../hooks/switch'
import { configState } from '../../store/config'
import { gymState } from '../../store/gym'
import { systemParametersState } from '../../store/system-parameters'
import { touristTokenState } from '../../store/tourist'
import { userState } from '../../store/user'
import { createTheme } from '../../theme/theme'
import s from './Root.module.css'

const Root: FC<RootProps> = (props) => {
  const setTouristToken = useSetRecoilState(touristTokenState)
  const setGym = useSetRecoilState(gymState)
  const setSystemParameters = useSetRecoilState(systemParametersState)
  const setUser = useSetRecoilState(userState)
  const [config, setConfig] = useRecoilState(configState)

  const theme = createTheme(config?.themeColor)
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  /**
   * If a new token provided in URL, it means loading in a different gym,
   * so mark the state falsy to avoid using previous storage value.
   */
  const safelyLoaded = useSwitch(!newTouristToken)

  const { data: integration } = useGet<Dto.Integration>(
    safelyLoaded.on && 'member_portal/website_integration'
  )

  useLayoutEffect(() => {
    const newUrl = new URL(url)

    if (newTouristToken) {
      setUser(undefined)
      setTouristToken(newTouristToken)
      newUrl.searchParams.delete('api_key')
      window.setTimeout(safelyLoaded.open, 100)
    }

    if (newConfig) {
      setConfig(JSON.parse(newConfig))
      newUrl.searchParams.delete('config')
    }

    if (integration) {
      const {
        customisation,
        gym,
        system_parameters: systemParameters,
      } = integration
      setConfig(customisation)
      setGym(gym)
      setSystemParameters(systemParameters)
      moment.tz.setDefault(gym.time_zone_name)
      dayjs.tz.setDefault(gym.time_zone_name)
    }

    if (newUrl.toString() === url.toString()) {
      return
    }

    window.history.replaceState(undefined, '', newUrl)
  }, [
    integration,
    safelyLoaded,
    setConfig,
    setGym,
    setSystemParameters,
    setTouristToken,
    setUser,
  ])

  if (!safelyLoaded.on) {
    return null
  }

  return (
    <ThemeProvider theme={theme}>
      <ConfirmProvider
        defaultOptions={{
          dialogProps: { fullWidth: false },
          titleProps: { variant: 'h1', textAlign: 'center', sx: { p: 4 } },
          cancellationButtonProps: { variant: 'outlined' },
          ...(downSm && {
            dialogActionsProps: {
              sx: {
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: theme.spacing(2),
              },
            },
          }),
        }}
      >
        <div
          {...props}
          {...createDataset({
            darkTheme: config
              ? new TinyColor(config.themeColor).isDark()
              : false,
          })}
          className={s.root}
          style={
            config && {
              '--theme-primary': config.themeColor,
              '--theme-secondary': `${config.themeColor}55`,
            }
          }
        />
      </ConfirmProvider>
    </ThemeProvider>
  )
}

const url = new URL(window.location.href)
const newTouristToken = url.searchParams.get('api_key')
const newConfig = url.searchParams.get('config')

type RootProps = PropsWithChildren & {}

declare module 'csstype' {
  interface Properties {
    '--theme-primary'?: string
    '--theme-secondary'?: string
  }
}

export { Root }
export type { RootProps }
